import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { downloadBlob } from "@/lib/download";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Landmark, ShieldCheck, ArrowUpFromLine, FileDown, Loader2 } from "lucide-react";
import TreasureSummary from "@/components/office/TreasureSummary";
import Transactions from "@/components/office/Transactions";
import ProofQueue from "@/components/office/ProofQueue";
import Expenses from "@/components/office/Expenses";
import { OpeningBalanceDialog } from "@/components/office/EditDialogs";

export default function Office() {
  const [scope, setScope] = useState("Galleon");
  const [data, setData] = useState(null);
  const [openingDlg, setOpeningDlg] = useState(false);
  const load = () => api.get("/island/treasury", { params: { scope } }).then((r) => setData(r.data)).catch(() => setData(null));
  useEffect(() => { load(); }, [scope]); // eslint-disable-line

  return (
    <div data-testid="office-page">
      <PageHeader eyebrow="Administrative Office" title="Treasure" description="Galleon and frigate balances, inflow from brothers and outflow by the Purse — every record traced from member to responsible officer.">
        {data?.can_manage && <Button variant="outline" className="gold-ring" onClick={() => setOpeningDlg(true)} data-testid="opening-balance-btn"><Landmark className="mr-1.5 h-4 w-4" />Set {scope} balance</Button>}
        <Button variant="outline" className="gold-ring" onClick={() => downloadBlob(`/island/treasury/statement.pdf?scope=${scope}`, `treasure-${scope.toLowerCase()}.pdf`)} data-testid="treasure-statement-btn"><FileDown className="mr-1.5 h-4 w-4" />Statement PDF</Button>
      </PageHeader>
      {data?.scopes?.length > 1 && (
        <div className="mb-5 flex flex-wrap gap-2" data-testid="treasure-scopes">
          {data.scopes.map((s) => <button key={s} onClick={() => setScope(s)} className={`rounded-full px-4 py-1.5 text-sm font-600 transition-colors ${scope === s ? "bg-[hsl(var(--gold))] text-[hsl(var(--primary-foreground))]" : "bg-secondary hover:bg-secondary/70"}`} data-testid={`scope-${s.toLowerCase()}`}>{s}</button>)}
        </div>
      )}
      {!data ? <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin" /></div> : (
        <Tabs defaultValue="treasure">
          <TabsList className="glass mb-4">
            <TabsTrigger value="treasure" data-testid="office-tab-treasure"><Landmark className="mr-1.5 h-4 w-4" />Treasure</TabsTrigger>
            {data.can_manage && <TabsTrigger value="verify" data-testid="office-tab-verify"><ShieldCheck className="mr-1.5 h-4 w-4" />Verify payments{data.pending_proofs > 0 && <Badge className="ml-2 bg-amber-500 text-[0.6rem] text-white" data-testid="verify-count">{data.pending_proofs}</Badge>}</TabsTrigger>}
            <TabsTrigger value="expenses" data-testid="office-tab-expenses"><ArrowUpFromLine className="mr-1.5 h-4 w-4" />Inflow & Outflow</TabsTrigger>
          </TabsList>
          <TabsContent value="treasure" className="space-y-4"><TreasureSummary data={data} /><Transactions rows={data.transactions} canManage={data.can_manage} categories={data.categories} inflowCategories={data.inflow_categories} onChanged={load} /></TabsContent>
          {data.can_manage && <TabsContent value="verify"><ProofQueue scope={scope} onChanged={load} /></TabsContent>}
          <TabsContent value="expenses"><Expenses scope={scope} categories={data.categories} inflowCategories={data.inflow_categories} onChanged={load} /></TabsContent>
        </Tabs>
      )}
      {openingDlg && <OpeningBalanceDialog scope={scope} opening={data?.opening} onClose={() => setOpeningDlg(false)} onSaved={load} />}
    </div>
  );
}
